import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import {
  Users,
  BarChart3,
  TrendingUp,
  Calendar,
  FileText,
  MoreHorizontal,
  ChevronDown,
  Download,
  Clock,
  AlertCircle,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  MessageSquare,
  Phone,
  Video,
  BookOpen,
  Zap,
  Grid3X3,
  Trophy,
  Medal,
  Award,
  Lightbulb,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/lib/utils';

// Shared Curiosity Analytics Data (from TeacherCuriosityAnalytics.jsx)
const MOCK_CHAPTERS = [
    {
    id: 1,
    name: 'Light and Reflection',
    subject: 'Physics',
    totalInteractions: 156,
    uniqueStudents: 7,
    coreUsage: 100,
    deepUsage: 56,
    avgCompletionRate: 72.3,
    },
    {
    id: 2,
    name: 'Motion and Forces',
    subject: 'Physics',
    totalInteractions: 142,
    uniqueStudents: 6,
    coreUsage: 95,
    deepUsage: 47,
    avgCompletionRate: 68.5,
    },
    {
    id: 3,
    name: 'Sound Waves',
    subject: 'Physics',
    totalInteractions: 128,
    uniqueStudents: 5,
    coreUsage: 88,
    deepUsage: 40,
    avgCompletionRate: 70.1,
    }
  ];

// Transform chapter data to Curiosity Insights format
const getCuriosityInsightsData = () => {
  const colors = ['emerald', 'teal', 'purple'];
  const trends = ['+12%', '+8%', '-3%']; // Mock trends - can be calculated from historical data later
  
  return MOCK_CHAPTERS.map((chapter, index) => ({
    id: chapter.id,
    topic: chapter.name,
    subject: chapter.subject,
    students: chapter.uniqueStudents,
    completion: Math.round(chapter.avgCompletionRate),
    color: colors[index % colors.length],
    trend: trends[index % trends.length],
    totalInteractions: chapter.totalInteractions,
    coreUsage: chapter.coreUsage,
    deepUsage: chapter.deepUsage
  }));
};

const ALL_CURIOSITY_DATA = getCuriosityInsightsData();


const upcomingDeadlinesData = [
  { id: 1, title: 'Physics Assignment Due', class: 'Grade 7A', due: 'Tomorrow', priority: 'high' },
  { id: 2, title: 'Parent Meeting', class: 'Grade 7B', due: 'Friday', priority: 'medium' },
  { id: 3, title: 'Report Card Review', class: 'All Classes', due: 'Next Week', priority: 'low' },
  ];

const StatCard = ({ data, isDark }) => (
  <Card className={cn(
    "border-none shadow-sm",
    isDark ? "bg-[hsl(var(--card))]" : data.bg
  )}>
    <CardContent className="p-6 relative overflow-hidden">
      <div className="flex justify-between items-start z-10 relative">
          <div>
          <p className={cn(
            "text-base font-medium mb-1",
            isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#2B2B2B]"
          )}>{data.title}</p>
          <h3 className={cn(
            "text-3xl font-bold",
            isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
          )}>{data.value}</h3>
        </div>
        <div className={cn(
          "p-2 rounded-xl",
          isDark ? "bg-[hsl(var(--gold-primary))]/20" : data.iconBg
        )}>
          <data.icon className={cn(
            "w-5 h-5",
            isDark ? "text-[hsl(var(--gold-primary))]" : data.text
          )} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ClassPerformanceCard = ({ data, isDark }) => (
  <div
    className={cn(
      "rounded-2xl p-5 border transition-all hover:shadow-md hover:scale-105 cursor-pointer text-left w-full h-full flex flex-col",
      isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] hover:border-[hsl(var(--gold-primary))]" : (
        data.color === 'blue' && "bg-gradient-to-br from-[#FAF8F5] to-[#F5F1EB] border-[#E8E1D6] hover:border-[#C9A961]" ||
        data.color === 'emerald' && "bg-gradient-to-br from-[#F5F1EB] to-[#F0EBE3] border-[#E8E1D6] hover:border-[#D4AF77]" ||
        data.color === 'purple' && "bg-gradient-to-br from-[#F0EBE3] to-[#E8E1D6] border-[#D4C4B0] hover:border-[#B8935A]" ||
        data.color === 'teal' && "bg-gradient-to-br from-[#FAF8F5] to-[#E8E1D6] border-[#D4C4B0] hover:border-[#A67C52]"
      )
    )}
    >
    <div className="w-full flex flex-col h-full">
      {/* Header Section */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={cn("p-2 rounded-lg shadow-sm flex-shrink-0",
            isDark ? "bg-[hsl(var(--card))] text-[hsl(var(--gold-primary))]" : (
              data.color === 'blue' && "bg-white text-[#C9A961]" ||
              data.color === 'emerald' && "bg-white text-[#D4AF77]" ||
              data.color === 'purple' && "bg-white text-[#B8935A]" ||
              data.color === 'teal' && "bg-white text-[#A67C52]"
            )
          )}>
            <Users className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className={cn("font-bold text-lg truncate mb-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{data.name}</h4>
            <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{data.students} students</p>
          </div>
        </div>
      </div>

      {/* Bottom Section - Average Score */}
      <div className="mt-auto pt-4 border-t" style={{borderColor: isDark ? 'hsl(var(--card-border))' : '#E8E1D6'}}>
        <div className="flex items-center justify-between">
          <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Average Score</p>
          <div className={cn(
            "px-3 py-1.5 rounded-lg text-base font-bold",
            isDark
              ? "bg-[hsl(var(--gold-primary))] text-white"
              : "bg-[#2B2B2B] text-white"
          )}>
            {data.score}%
          </div>
        </div>
      </div>
    </div>
  </div>
);



const LeaderboardRow = ({ data, isDark, period }) => {
  const getDisplayXP = () => {
    if (typeof data.xp === 'object' && data.xp !== null) {
      const periodXP = data.xp[period];
      const allTimeXP = data.xp.all_time;
      const xpValue = periodXP ?? allTimeXP ?? 0;
      return xpValue.toLocaleString();
    }
    const xpValue = data.xp ?? 0;
    return xpValue.toLocaleString();
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between p-3 rounded-xl mb-2 cursor-pointer transition-all hover:scale-[1.02]",
        isDark
          ? "bg-[hsl(var(--card))] hover:bg-[hsl(var(--card-border))]/50"
          : "bg-[#FAF8F5] border border-transparent hover:border-[#D4AF77] hover:shadow-sm"
      )}>
      <div className="flex items-center gap-3 flex-1">
        <span className="text-lg">👑</span>
        <img src={data.avatar} alt={data.name} className={cn("w-9 h-9 rounded-full border-2", isDark ? "border-[hsl(var(--card-border))]" : "border-white shadow-sm")} />
        <div>
          <h5 className={cn("text-base font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{data.name}</h5>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{data.class}</span>
          </div>
        </div>
      </div>
      <span className={cn("text-base font-bold px-3 py-1 rounded-lg whitespace-nowrap ml-2", isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#2B2B2B] text-white")}>
        {getDisplayXP()} XP
      </span>
    </div>
  );
};

const ClassInsightsPanel = ({ currentClass, isDark, navigate, setPerformanceFilter }) => {
  const performance = currentClass?.performance || {};
  const needsHelp = Number(performance.needsHelp || 0);
  const onTrack = Number(performance.onTrack || 0);

  const handleNavigateToStudents = (filterType) => {
    if (setPerformanceFilter) {
      setPerformanceFilter(filterType);
    }
    try { trackEvent('navigate_to_students', attachUserProps({ filter: filterType, page: 'teacher_dashboard' })); } catch (e) {}
    navigate('/teacher/my-classes');
  };

  return (
    <div className={cn(
      "rounded-2xl p-5 border",
      isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-[#FAF8F5] border-[#E8E1D6]"
    )}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => handleNavigateToStudents('fair')}
          className={cn(
            "flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-105 cursor-pointer text-left",
            isDark ? "hover:bg-[hsl(var(--card-border))]/50" : "hover:bg-[#F0EBE3]/60"
          )}
        >
          <div className={cn(
            "p-2.5 rounded-xl flex-shrink-0",
            isDark ? "bg-[hsl(var(--gold-primary))]/15 text-[hsl(var(--gold-primary))]" : "bg-white text-[#C9A961]"
          )}>
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>On Track</div>
            <div className={cn("text-xl font-bold leading-tight", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{onTrack}</div>
          </div>
        </button>

        <button
          onClick={() => handleNavigateToStudents('needs-help')}
          className={cn(
            "flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-105 cursor-pointer text-left",
            isDark ? "hover:bg-[hsl(var(--card-border))]/50" : "hover:bg-[#F0EBE3]/60"
          )}
        >
          <div className={cn(
            "p-2.5 rounded-xl flex-shrink-0",
            isDark ? "bg-[hsl(var(--gold-primary))]/15 text-[hsl(var(--gold-primary))]" : "bg-white text-[#B8935A]"
          )}>
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Needs Help</div>
            <div className={cn("text-xl font-bold leading-tight", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{needsHelp}</div>
          </div>
        </button>

            <div className="flex items-center gap-3">
          <div className={cn(
            "p-2.5 rounded-xl",
            isDark ? "bg-[hsl(var(--gold-primary))]/15 text-[hsl(var(--gold-primary))]" : "bg-white text-[#A67C52]"
          )}>
            <Zap className="w-5 h-5" />
              </div>
              <div>
            <div className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Class Activity</div>
            <div className={cn("text-xl font-bold leading-tight", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass?.engagement || 0}%</div>
              </div>
            </div>
                    </div>
                  </div>
  );
};

const CuriosityCard = ({ data, isDark }) => (
  <div className={cn(
    "rounded-xl p-4 border shadow-sm flex flex-col justify-between min-w-0 transition-all hover:shadow-lg",
    isDark
      ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--sidebar-hover))] border-[hsl(var(--card-border))]"
      : "bg-gradient-to-br from-white via-[#FAF8F5] to-[#F5F1EB] border-[#E8E1D6]"
  )}>
    {/* Header with icon and metric */}
    <div className="flex items-start justify-between mb-3">
      <div className={cn(
        "p-2 rounded-lg shadow-sm",
        isDark
          ? "bg-[hsl(var(--gold-primary))]/20 text-[hsl(var(--gold-primary))]"
          : data.color === 'emerald'
            ? "bg-[#C9A961]/15 text-[#C9A961] shadow-[#C9A961]/20"
            : data.color === 'teal'
              ? "bg-[#D4AF77]/15 text-[#D4AF77] shadow-[#D4AF77]/20"
              : "bg-[#C9A961]/15 text-[#C9A961] shadow-[#C9A961]/20"
      )}>
        {data.color === 'emerald' ? <Zap className="w-5 h-5" /> :
          data.color === 'teal' ? <BookOpen className="w-5 h-5" /> :
            <Star className="w-5 h-5" />}
      </div>
      <div className="text-right">
        <div className={cn(
          "text-sm font-bold px-2 py-1 rounded-lg mb-1",
          isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#2B2B2B] text-white"
        )}>
          {data.completion}%
        </div>
        <div className={cn(
          "text-xs font-medium uppercase tracking-wider",
          isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#2B2B2B]"
        )}>
          Engagement
        </div>
      </div>
            </div>

    {/* Topic and students */}
    <div className="space-y-1 mb-3">
      <h4 className={cn(
        "font-bold text-base leading-tight",
        isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
      )}>
        {data.topic}
      </h4>
      <p className={cn(
        "text-xs font-medium flex items-center gap-2",
        isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"
      )}>
        <Users className="w-3 h-3" />
        <span className={cn("px-2 py-0.5 rounded text-xs font-semibold", isDark ? "bg-[hsl(var(--gold-primary))]/20 text-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]/10 text-[#2B2B2B]")}>
          {data.students} students
        </span>
                    </p>
                  </div>

    {/* Progress bar */}
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className={cn(
          "font-semibold uppercase tracking-wider opacity-80",
          isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
        )}>
          Trend
                      </span>
        <span className={cn(
          "text-sm font-bold px-2 py-1 rounded-lg",
          isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#2B2B2B] text-white"
        )}>
          {data.trend}
                      </span>
                    </div>
      <div className={cn(
        "h-2 w-full rounded-full overflow-hidden shadow-inner",
        isDark ? "bg-[hsl(var(--card-border))]" : "bg-[#F0EBE0]"
      )}>
        <div
          className={cn(
            "h-full rounded-full transition-all duration-1000 ease-out",
            isDark
              ? "bg-gradient-to-r from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-secondary))]"
              : data.color === 'emerald'
                ? "bg-gradient-to-r from-[#C9A961] to-[#D4AF77]"
                : data.color === 'teal'
                  ? "bg-gradient-to-r from-[#D4AF77] to-[#C9A961]"
                  : "bg-gradient-to-r from-[#C9A961] to-[#D4AF77]"
          )}
          style={{ width: `${data.completion}%` }}
        />
                    </div>
                  </div>
                </div>
);

// --- Main Component ---

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { teacherTheme, classes, currentClass, setCurrentClass, user } = useTeacher();
  const { toast } = useToast();
  const viewMode = currentClass ? 'individual' : 'overview';
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all_time');
  const [stats, setStats] = useState(null);
  const [classPerformance, setClassPerformance] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState({
    stats: true,
    performance: true,
    leaderboard: true,
    insights: false,
  });

  // Mock download handler for Generate Report
  const handleGenerateReport = () => {
    const reportContent = `Dashboard Report\n${'='.repeat(50)}\n\nGenerated on: ${new Date().toLocaleDateString()}\n\nThis section will display:\n- Student performance summary\n- Class analytics\n- Attendance records\n- Assessment results\n\nBackend will integrate real data here.\n`;
    
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dashboard_report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Report Generated",
      description: "Dashboard report downloaded successfully",
    });
    try { trackEvent('dashboard_report_generated', attachUserProps({ page: 'teacher_dashboard' })); } catch (e) {}
  };

  const token = getAuthToken('teacher');

  const classParam = currentClass ? `?class_id=${currentClass.id}` : '';

  const loadStats = async () => {
    setLoading((prev) => ({ ...prev, stats: true }));
    try {
      const res = await axios.get(apiUrl(`v1/teachers/dashboard/stats${classParam}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (e) {
      console.error('Failed to fetch stats', e);
      setStats(null);
    } finally {
      setLoading((prev) => ({ ...prev, stats: false }));
    }
  };

  const loadClassPerformance = async () => {
    setLoading((prev) => ({ ...prev, performance: true }));
    try {
      const res = await axios.get(apiUrl(`v1/teachers/dashboard/class-performance${classParam}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res?.data?.classes || [];
      const mapped = data.map((item, idx) => ({
        ...item,
        color: ['blue', 'emerald', 'purple', 'teal'][idx % 4],
      }));
      setClassPerformance(mapped);
    } catch (e) {
      console.error('Failed to fetch class performance', e);
      setClassPerformance([]);
    } finally {
      setLoading((prev) => ({ ...prev, performance: false }));
    }
  };

  const loadInsights = async () => {
    if (!currentClass) {
      setInsights(null);
      return;
    }
    setLoading((prev) => ({ ...prev, insights: true }));
    try {
      const res = await axios.get(apiUrl(`v1/teachers/dashboard/class-insights/?class_id=${currentClass.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(res.data);
    } catch (e) {
      console.error('Failed to fetch class insights', e);
      setInsights(null);
    } finally {
      setLoading((prev) => ({ ...prev, insights: false }));
    }
  };

  const loadLeaderboard = async (period) => {
    setLoading((prev) => ({ ...prev, leaderboard: true }));
    try {
      const res = await axios.get(apiUrl(`v1/teachers/dashboard/leaderboard/?period=${period}${classParam ? `&class_id=${currentClass?.id}` : ''}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(res?.data?.students || []);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
      setLeaderboardData([]);
    } finally {
      setLoading((prev) => ({ ...prev, leaderboard: false }));
    }
  };

  useEffect(() => {
    if (!token) return;
    loadStats();
    loadClassPerformance();
    loadInsights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass?.id]);

  useEffect(() => {
    if (!token) return;
    loadLeaderboard(leaderboardPeriod);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboardPeriod, currentClass?.id]);

  const topPerformers = leaderboardData.map((item) => {
    const avatar = item.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(item.name || 'Student')}`;
    return {
      name: item.name || 'Student',
      class: item.class || 'Class',
      score: item.score || 0,
      xp: item.xp || {},
      avatar,
    };
  });

  // Get Curiosity Data (keep mock)
  const getCuriosityData = () => {
    // Always use the latest data from MOCK_CHAPTERS
    const insightsData = getCuriosityInsightsData();
    
    if (viewMode === 'overview') {
      return insightsData.slice(0, 3); // Top 3 chapters
    } else {
      // Filter by subject if class is selected
      const classSubject = currentClass?.subject || 'Physics';
      return insightsData
        .filter(item => item.subject === classSubject)
        .slice(0, 3);
    }
  };


  const curiosityData = getCuriosityData();

  // Update the stats data based on current view
  const getStatsData = () => {
    const cardBase = [
      {
        title: viewMode === 'overview' ? 'Total Students' : 'Students',
        value: stats ? (stats.total_students || 0).toLocaleString() : '—',
        trend: '↑ 0%',
        trendUp: true,
        icon: Users,
        bg: 'bg-[#E8DCC8]',
        text: 'text-[#B8935A]',
        iconBg: 'bg-white'
      },
      {
        title: viewMode === 'overview' ? 'Active Classes' : 'Avg Score',
        value: viewMode === 'overview'
          ? (stats ? stats.active_classes || 0 : 0)
          : `${stats ? stats.avg_performance || 0 : 0}%`,
        trend: '↑ 0%',
        trendUp: true,
        icon: viewMode === 'overview' ? BarChart3 : TrendingUp,
        bg: 'bg-[#D4C4A8]',
        text: 'text-[#A67C52]',
        iconBg: 'bg-white'
      },
    ];

    if (viewMode === 'overview') {
      cardBase.push({
        title: 'Avg Performance',
        value: `${stats ? stats.avg_performance || 0 : 0}%`,
        trend: '↑ 0%',
        trendUp: true,
        icon: TrendingUp,
        bg: 'bg-[#C9B896]',
        text: 'text-[#8B6F3F]',
        iconBg: 'bg-white'
      });
    }
    return cardBase;
  };

  const classPerformanceData = classPerformance.length > 0 ? classPerformance : [];

  const getUpcomingDeadlines = () => {
    if (viewMode === 'overview') {
      return upcomingDeadlinesData;
    } else {
      // Filter for the specific class + "All Classes" events
      return upcomingDeadlinesData.filter(item =>
        item.class === currentClass.name || item.class === 'All Classes'
      );
    }
  };

  const upcomingDeadlines = getUpcomingDeadlines();

  return (
    <div className={cn(
      "w-full h-full overflow-y-auto p-6 lg:p-8 space-y-6",
      teacherTheme === 'dark' ? "bg-[hsl(var(--background))]" : "bg-[#FAF8F5]"
    )}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className={cn("text-3xl font-bold", teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
            {viewMode === 'overview' ? `Welcome back, ${user.name.split(' ')[0]}!` : currentClass.fullName}
          </h1>
          <p className={cn("text-sm font-medium mt-2", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
            {viewMode === 'overview' ? "Here's what's happening in your classes today." : "Class Performance & Insights"}
          </p>
        </div>

        <Button 
          onClick={handleGenerateReport}
          className={cn(
          "h-9 text-sm font-medium rounded-lg shadow-sm",
          teacherTheme === 'dark'
            ? "bg-[hsl(var(--gold-primary))] hover:bg-[hsl(var(--gold-medium))] text-white"
            : "bg-[#2B2B2B] hover:bg-[#1A1A1A] text-white"
        )}>
          Generate Report
        </Button>
      </div>

      {/* Row 1: Stats Cards */}
      <div className={cn(
        "w-full grid grid-cols-1 md:grid-cols-2 gap-4",
        viewMode === 'overview' ? "lg:grid-cols-3" : "lg:grid-cols-2"
      )}>
        {getStatsData().map((stat, index) => (
          <StatCard key={index} data={stat} isDark={teacherTheme === 'dark'} />
        ))}
      </div>

      {/* Row 2: Main Grid */}
      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Col 1 & 2: Class Performance Overview / Class Insights */}
        <div className="lg:col-span-2">
          {viewMode === 'overview' ? (
            <Card className={cn(
              "border-none shadow-sm h-full",
              teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
            )}>
              <CardHeader className={cn(
                "pb-2 rounded-t-xl",
                teacherTheme === 'dark'
                  ? "bg-[hsl(var(--card))]"
                  : "bg-gradient-to-r from-[#FAF8F5] to-[#F5F1EB]"
              )}>
                <CardTitle className={cn(
                  "text-2xl font-bold tracking-tight",
                  teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
                )}>
                  Class Performance Overview
                </CardTitle>
              </CardHeader>
              <CardContent className={cn(
                "pt-6",
                teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
              )}>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                  {classPerformanceData.map((item) => (
                    <ClassPerformanceCard key={item.id} data={item} isDark={teacherTheme === 'dark'} />
              ))}
            </div>
          </CardContent>
        </Card>
          ) : (
            <Card className={cn(
              "border-none shadow-sm h-full",
              teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
            )}>
              <CardHeader className={cn(
                "pb-2 rounded-t-xl",
                teacherTheme === 'dark'
                  ? "bg-[hsl(var(--card))]"
                  : "bg-gradient-to-r from-[#FAF8F5] to-[#F5F1EB]"
              )}>
                <CardTitle className={cn(
                  "text-2xl font-bold tracking-tight",
                  teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
                )}>
                  Class Insights
                </CardTitle>
              </CardHeader>
              <CardContent className={cn(
                "pt-6",
                teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
              )}>
                <ClassInsightsPanel
                  currentClass={{
                    ...currentClass,
                    performance: {
                      onTrack: insights?.on_track || 0,
                      needsHelp: insights?.needs_help || 0,
                    },
                    engagement: insights?.class_activity || 0
                  }}
                  isDark={teacherTheme === 'dark'}
                  navigate={navigate}
                  setPerformanceFilter={() => {}}
                />
              </CardContent>
            </Card>
          )}
              </div>

        {/* Col 3: Top Performers */}
        <div className="lg:col-span-1">
          <Card className={cn(
            "border-none shadow-sm h-full",
            teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
          )}>
            <CardHeader className={cn(
              "pb-2 flex flex-row items-center justify-between space-y-0 rounded-t-xl",
              teacherTheme === 'dark'
                ? "bg-[hsl(var(--card))]"
                : "bg-gradient-to-r from-[#F5F1EB] to-[#FAF8F5]"
            )}>
              <CardTitle className={cn(
                "text-2xl font-bold tracking-tight flex items-center gap-2",
                teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
              )}>
                <Trophy className={cn("w-5 h-5", teacherTheme === 'dark' ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                Leaderboard
              </CardTitle>
            </CardHeader>
            {/* Period Filter Buttons */}
            <div className={cn("px-6 pt-4 pb-2 flex gap-3 w-full", teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white")}>
              <button
                onClick={() => { setLeaderboardPeriod('all_time'); try { trackEvent('leaderboard_period_change', attachUserProps({ period: 'all_time', page: 'teacher_dashboard' })); } catch (e) {} }}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all",
                  leaderboardPeriod === 'all_time'
                    ? teacherTheme === 'dark'
                      ? "bg-[hsl(var(--gold-primary))] text-white"
                      : "bg-[#2B2B2B] text-white"
                    : teacherTheme === 'dark'
                      ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80"
                      : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
                )}
              >
                All Time
              </button>
              <button
                onClick={() => { setLeaderboardPeriod('weekly'); try { trackEvent('leaderboard_period_change', attachUserProps({ period: 'weekly', page: 'teacher_dashboard' })); } catch (e) {} }}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all",
                  leaderboardPeriod === 'weekly'
                    ? teacherTheme === 'dark'
                      ? "bg-[hsl(var(--gold-primary))] text-white"
                      : "bg-[#2B2B2B] text-white"
                    : teacherTheme === 'dark'
                      ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80"
                      : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
                )}
              >
                Weekly
              </button>
              <button
                onClick={() => { setLeaderboardPeriod('monthly'); try { trackEvent('leaderboard_period_change', attachUserProps({ period: 'monthly', page: 'teacher_dashboard' })); } catch (e) {} }}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-all",
                  leaderboardPeriod === 'monthly'
                    ? teacherTheme === 'dark'
                      ? "bg-[hsl(var(--gold-primary))] text-white"
                      : "bg-[#2B2B2B] text-white"
                    : teacherTheme === 'dark'
                      ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80"
                      : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
                )}
                    >
                Monthly
              </button>
                    </div>
            <CardContent className={cn(
              "pt-6 space-y-1",
              teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
            )}>
              {topPerformers.length > 0 ? (
                topPerformers.map((item) => (
                  <LeaderboardRow key={item.name} data={item} isDark={teacherTheme === 'dark'} period={leaderboardPeriod} />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <span className={cn("text-sm font-medium", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>No leaderboard data available for this class.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 3: Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 w-full">
        {/* Col 1 & 2: Curiosity Insights */}
        <div className="lg:col-span-3 w-full">
          <Card className={cn(
            "flex flex-col h-full border-none shadow-sm",
            teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-[#FAF8F5] border-[#E8E1D6]"
          )}>
            <CardHeader className={cn(
              "pb-2 rounded-t-xl flex flex-row items-center justify-between",
              teacherTheme === 'dark'
                ? "bg-[hsl(var(--card))]"
                : "bg-gradient-to-r from-[#FAF8F5] to-[#F5F1EB]"
            )}>
              <div className="flex items-center gap-2">
                <CardTitle className={cn(
                  "text-2xl font-bold tracking-tight flex items-center gap-2",
                  teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
                )}>
                  <Lightbulb className={cn("w-5 h-5", teacherTheme === 'dark' ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                  Curiosity Insights
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-10 w-10 rounded-lg transition-all", teacherTheme === 'dark' ? "text-[hsl(var(--gold-primary))] hover:text-[hsl(var(--gold-medium))] hover:bg-[hsl(var(--gold-primary))]/15" : "text-[#C9A961] hover:text-[#B8935A] hover:bg-[#C9A961]/15")}
                onClick={() => navigate('/teacher/curiosity-analytics')}
              >
                <BarChart3 className="w-6 h-6" />
              </Button>
            </CardHeader>
            <CardContent className={cn(
              "pt-6 flex-1 flex flex-col",
              teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
            )}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 min-w-0">
                {curiosityData.length > 0 ? (
                  curiosityData.map((data) => (
                    <CuriosityCard key={data.id} data={data} isDark={teacherTheme === 'dark'} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center w-full col-span-3 h-full min-h-[150px] text-center">
                    <Lightbulb className={cn("w-8 h-8 mb-2 opacity-50", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                    <span className={cn("text-sm font-medium", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>No curiosity insights available.</span>
                  </div>
                )}
                </div>
            </CardContent>
          </Card>
        </div>


        {/* Col 3 & 4: Upcoming Deadlines */}
        <div className="lg:col-span-2 w-full">
          <Card className={cn(
            "border-none shadow-sm h-full",
            teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
          )}>
            <CardHeader className={cn(
              "pb-2 rounded-t-xl",
              teacherTheme === 'dark'
                ? "bg-[hsl(var(--card))]"
                : "bg-gradient-to-r from-[#F5F1EB] to-[#FAF8F5]"
            )}>
              <div className="flex items-center justify-between w-full">
                <CardTitle className={cn(
                  "text-2xl font-bold tracking-tight",
                  teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
                )}>Upcoming Deadlines</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-10 w-10 rounded-lg transition-all", teacherTheme === 'dark' ? "text-[hsl(var(--gold-primary))] hover:text-[hsl(var(--gold-medium))] hover:bg-[hsl(var(--gold-primary))]/15" : "text-[#C9A961] hover:text-[#B8935A] hover:bg-[#C9A961]/15")}
                  onClick={() => navigate('/teacher/calendar')}
                >
                  <Calendar className="w-6 h-6" />
                </Button>
            </div>
            </CardHeader>
            <CardContent className={cn(
              "pt-6 space-y-3",
              teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-white"
            )}>
              {upcomingDeadlines.length > 0 ? (
                upcomingDeadlines.map((item) => (
                  <div key={item.id} className={cn("flex items-center justify-between p-3 rounded-xl", teacherTheme === 'dark' ? "bg-[hsl(var(--card))]" : "bg-[#FAF8F5]")}>
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg",
                        teacherTheme === 'dark' ? "bg-[hsl(var(--gold-primary))]/20 text-[hsl(var(--gold-primary))]" : (
                          item.priority === 'high' ? "bg-[#F5E8E4] text-[#A67C52]" :
                            item.priority === 'medium' ? "bg-[#F5F1EB] text-[#C9A961]" :
                              "bg-[#C9A961]/20 text-[#C9A961]"
                        )
                      )}>
                        <Calendar className="w-4 h-4" />
                      </div>
                    <div>
                        <h5 className={cn("text-sm font-bold leading-tight", teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{item.title}</h5>
                        <p className={cn("text-xs mt-0.5", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                          {viewMode === 'overview' ? item.class : currentClass?.name || item.class}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={cn("text-sm font-bold", teacherTheme === 'dark' ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{item.due}</p>
                      <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded-full uppercase inline-block mt-0.5 text-white",
                        teacherTheme === 'dark' ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]"
                      )}>
                        {item.priority}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Calendar className={cn("w-12 h-12 mb-3 opacity-20", teacherTheme === 'dark' ? "text-white" : "text-[#6B6B6B]")} />
                  <p className={cn("text-sm font-medium", teacherTheme === 'dark' ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>No upcoming deadlines</p>
              </div>
              )}
            </CardContent>
          </Card>
        </div>


      </div>

    </div>
  );
}
